// Firestore persistence, not a Vuex module — see ../firestoreDraft.js.
// Divii integration code — replaces the upstream localStorage store.
// Same load/save/clear API, backed by agreements/{agreementId}/holidaySchedule/draft.
//
// NOTE: this is deliberately the SAME document the pre-existing Holiday Schedule
// tab (src/holiday-schedule/) writes to, so existing holiday decisions carry over
// and rolling the feature flag back loses nothing. The migrate() below is
// upstream's — a superset of the old copy's, so old documents load unchanged.

import { createFirestoreDraft } from '../firestoreDraft.js';
import { buildInitialHolidays, SEED_HOLIDAYS } from '@/lib/parentingSchedule/seed/holidays';

const SEED_BY_ID = Object.fromEntries(SEED_HOLIDAYS.map((h) => [h.id, h]));

// Migrate legacy state to the current shape. Done on load so users keep their tweaks.
export function migrate(holidays) {
	return holidays.map((h) => {
		let next = h;
		if (next?.rule?.kind === 'agreement-required') {
			next = { ...next, rule: { kind: 'unset' } };
		}
		if (next?.rule?.kind === 'noon-to-noon-alternate') {
			next = {
				...next,
				rule: {
					kind: 'paired-day-split',
					evenParent: next.rule.evenParent || 'A',
					splitTime: '12:00',
				},
			};
		}
		if (next.longWeekendType === undefined) {
			next = { ...next, longWeekendType: SEED_BY_ID[next.id]?.longWeekendType ?? null };
		}
		if (next.whyThisMatters === undefined) {
			next = { ...next, whyThisMatters: '' };
		}
		if (next.flaggedForDiscussion === undefined) {
			next = { ...next, flaggedForDiscussion: false };
		}
		if (next.timesByAgreement === undefined) {
			next = { ...next, timesByAgreement: false };
		}
		if (next.discussionNote === undefined) {
			next = { ...next, discussionNote: null };
		}
		if (next.customName === undefined) {
			next = { ...next, customName: false };
		}
		if (next.dateRange === undefined) {
			next = { ...next, dateRange: null };
		}
		if (next.dateRanges === undefined) {
			next = { ...next, dateRanges: null };
		}
		// Legacy shape (pre-attribution beta): plain string. Wrap non-empty
		// strings in the object shape so authorName/updatedAt exist; treat
		// empty strings as null so the "has note?" check stays simple.
		if (typeof next.discussionNote === 'string') {
			const text = next.discussionNote;
			next = {
				...next,
				discussionNote:
					text && text.trim().length > 0
						? { text, authorName: '', updatedAt: null }
						: null,
			};
		}
		return next;
	});
}

const store = createFirestoreDraft({
	collection: 'holidaySchedule',
	field: 'holidays',
	migrate,
	seed: (partyAName, partyBName, jurisdiction) =>
		buildInitialHolidays(partyAName, partyBName, jurisdiction),
	isEmpty: (raw) => !Array.isArray(raw) || raw.length === 0,
	log: '[holiday-schedule]',
});

export const { load, save, flushSave, clear, onError } = store;
